/**
 * Traffic — ambient cars streaming down the lanes, plus the choreographed
 * "killer" car that arrives exactly when a doomed hop lands.
 *
 * Rules that keep it readable and fair-looking:
 *  - cars only use lanes the chicken hasn't passed (barriers "block" the rest)
 *  - while a hop is in flight, ambient cars in the target lane floor it so
 *    they clear the manhole before the chicken lands (a free near-miss)
 *  - the killer lane gets no ambient spawns while the kill is in flight
 */
import { MANHOLE_Y, laneCenterX, VIEW_H } from './Layout.js';

const SPAWN_Y = -160;

export default class Traffic {
  constructor(sprites, bus) {
    this.sprites = sprites;
    this.bus = bus;
    this.cars = [];
    this.spawnIn = 0.4;
    this.ctx = { minLane: 1, maxLane: 24, blockedLane: null };
  }

  /** Called by the scene whenever the round state changes. */
  setContext({ minLane, maxLane, blockedLane = null }) {
    this.ctx = { minLane, maxLane, blockedLane };
  }

  clear() {
    this.cars = [];
  }

  /** A car whose center crosses the manhole row exactly at `arrivalMs`. */
  dispatchKiller(lane, arrivalMs) {
    const speed = (MANHOLE_Y - SPAWN_Y) / (arrivalMs / 1000);
    this.cars.push(this._makeCar(lane, speed, true));
  }

  /** Ambient cars already heading for `lane` floor it to clear in time. */
  rushLane(lane, clearMs) {
    for (const car of this.cars) {
      if (car.lane !== lane || car.killer) continue;
      const gap = MANHOLE_Y + 140 - car.y;
      if (gap > 0) {
        const needed = gap / (clearMs / 1000);
        car.speed = Math.max(car.speed, needed);
      }
    }
  }

  _makeCar(lane, speed, killer = false) {
    // ~30% of ambient traffic carries the sponsor livery (never the killer)
    const r = Math.random();
    const spr = killer
      ? this.sprites.cars[(Math.random() * this.sprites.cars.length) | 0]
      : r < 0.3
        ? this.sprites.adVan
        : r < 0.4
          ? this.sprites.fireTruck
          : this.sprites.cars[(Math.random() * this.sprites.cars.length) | 0];
    // the sponsor van cruises slowly so the ad stays readable
    if (spr === this.sprites.adVan) speed *= 0.55;
    return { lane, x: laneCenterX(lane), y: SPAWN_Y, speed, spr, killer, whooshed: false };
  }

  _laneIsClearToSpawn(lane) {
    for (const car of this.cars) {
      if (car.lane !== lane) continue;
      // slow sponsor vans need a longer gap so faster cars never rear-end them
      const clearance = car.spr === this.sprites.adVan ? 640 : 320;
      if (car.y < SPAWN_Y + clearance) return false;
    }
    return true;
  }

  update(dt, camX) {
    const { minLane, maxLane, blockedLane } = this.ctx;

    this.spawnIn -= dt;
    if (this.spawnIn <= 0) {
      this.spawnIn = 0.30 + Math.random() * 0.55;
      const options = [];
      for (let l = minLane; l <= maxLane; l++) {
        if (l === blockedLane || !this._laneIsClearToSpawn(l)) continue;
        // keep ambient traffic where the player can see it
        const x = laneCenterX(l) - camX;
        if (x < 60 || x > 1360) continue;
        options.push(l);
      }
      if (options.length) {
        const lane = options[(Math.random() * options.length) | 0];
        this.cars.push(this._makeCar(lane, 640 + Math.random() * 360));
      }
    }

    for (const car of this.cars) {
      car.y += car.speed * dt;
    }
    this.cars = this.cars.filter((c) => c.y < VIEW_H + 220);
  }

  draw(ctx, camX) {
    for (const car of this.cars) {
      const x = car.x - camX;
      if (x < -140 || x > 1420) continue;
      const spr = car.spr;
      // soft moving shadow
      ctx.fillStyle = 'rgba(18,20,26,0.22)';
      ctx.beginPath();
      ctx.ellipse(x + 5, car.y + 8, spr.logicalW * 0.42, spr.logicalH * 0.46, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.drawImage(spr, x - spr.logicalW / 2, car.y - spr.logicalH / 2, spr.logicalW, spr.logicalH);
    }
  }
}
